/**
 * One legal hold in the Legal holds tab (owner decision 2026-10-16, migration 370).
 *
 * Pure: everything arrives in props and every action is a callback, so
 * render-tests/legal-holds.render.test.tsx draws it to HTML. It never shows the image
 * or a link to it. The member data appears only after an audited view, which needs a
 * reason. The report id is recorded once; it starts the 1-year life of the member data.
 */
import { useState } from 'react';
import { AlertTriangle, CheckCircle, Loader2, Lock, Scale } from 'lucide-react';
import { REASON_LABELS } from './refused-photos-model';
import { networkRegionText, reportIdProblem, reportState, type LegalHold, type LegalHoldSubject } from './legal-holds-model';

const MIN_REASON = 5;

export interface LegalHoldCardProps {
  hold: LegalHold;
  now?: Date;
  /** The member data from an audited view in this session, or null. */
  subject: LegalHoldSubject | null;
  busy: boolean;
  error: string | null;
  onShowSubject: (reason: string) => void;
  onRecordReport: (reportId: string) => void;
}

const TONE_CLASS: Record<string, string> = {
  done: 'border-green-200 bg-green-50 text-green-800',
  todo: 'border-amber-200 bg-amber-50 text-amber-800',
  overdue: 'border-destructive bg-destructive/10 text-destructive',
  none: 'border-muted bg-muted/40 text-muted-foreground',
};

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all font-medium">{value && value.length > 0 ? value : '—'}</dd>
    </div>
  );
}

export function LegalHoldCard({ hold, now = new Date(), subject, busy, error, onShowSubject, onRecordReport }: LegalHoldCardProps) {
  const [reason, setReason] = useState('');
  const [reportId, setReportId] = useState('');
  const state = reportState(hold, now);
  const reported = state.tone === 'done';
  const idProblem = reportId.trim().length > 0 ? reportIdProblem(reportId) : null;
  const name = [subject?.first_name, subject?.last_name].filter(Boolean).join(' ');

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4" aria-busy={busy ? 'true' : 'false'}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive">
          <Scale className="h-3 w-3" /> Legal hold
        </span>
        <span className="font-mono text-xs text-muted-foreground">Photo {hold.photo_id}</span>
        {hold.reasons.map((r) => (
          <span key={r} className="rounded-full bg-muted px-2 py-0.5 text-xs">{REASON_LABELS[r] ?? r}</span>
        ))}
      </div>
      <div className="text-xs text-muted-foreground">
        Held on {hold.decided_at.slice(0, 10)} by admin <span className="font-mono">{hold.decided_by ?? 'unknown'}</span>.
        Account id <span className="font-mono">{hold.user_id}</span>.
      </div>

      <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Do not open, download or forward the file. It is kept at <span className="font-mono">legal-hold/{hold.photo_id}</span>,
          and nothing deletes it.
        </span>
      </div>

      <div
        role={state.tone === 'overdue' ? 'alert' : 'status'}
        className={`flex items-start gap-2 rounded-md border p-2 text-xs ${TONE_CLASS[state.tone]}`}
      >
        {reported ? <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
        <span>{state.text}</span>
      </div>

      {hold.has_subject && (
        subject ? (
          <div className="space-y-1 rounded-md border p-3">
            <div className="text-xs font-semibold">Member data for the report</div>
            <dl className="space-y-1">
              <Field label="E-mail" value={subject.email} />
              <Field label="Phone" value={subject.phone_e164} />
              <Field label="Name" value={name} />
              <Field label="Display name" value={subject.display_name} />
              <Field label="Birthday" value={subject.birthday?.slice(0, 10)} />
              <Field label="City" value={subject.city} />
              <Field label="Country" value={subject.country} />
              <Field label="Network region of the last request" value={networkRegionText(subject.last_request_country)} />
              <Field label="Account id" value={subject.user_id} />
              <Field label="Kept since" value={subject.captured_at.slice(0, 10)} />
            </dl>
            <div className="text-xs text-muted-foreground">This view was logged.</div>
          </div>
        ) : (
          <div className="space-y-2 rounded-md border p-3">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why do you need the member data? (for example: filing the CyberTipline report)"
              aria-label="Reason to see the member data"
              className="w-full rounded-lg border bg-background p-2 text-xs"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy || reason.trim().length < MIN_REASON}
                onClick={() => onShowSubject(reason)}
                className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50"
              >
                {busy && <Loader2 className="h-3 w-3 animate-spin" />} Show member data
              </button>
              <span className="text-xs text-muted-foreground">Every view is logged.</span>
            </div>
          </div>
        )
      )}

      {hold.has_subject && !reported && (
        <div className="space-y-2 rounded-md border p-3">
          <input
            value={reportId}
            onChange={(e) => setReportId(e.target.value)}
            placeholder="CyberTipline report id"
            aria-label="CyberTipline report id"
            className="w-full rounded-lg border bg-background p-2 font-mono text-xs"
          />
          {idProblem && <div className="text-xs text-destructive">{idProblem}</div>}
          <button
            type="button"
            disabled={busy || reportId.trim().length === 0 || idProblem !== null}
            onClick={() => onRecordReport(reportId)}
            className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
          >
            {busy && <Loader2 className="h-3 w-3 animate-spin" />} Save report id
          </button>
          <div className="text-xs text-muted-foreground">
            The member data is deleted 1 year after the report (18 U.S.C. 2258A(h)). You cannot change the id later.
          </div>
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
      )}
    </div>
  );
}
